import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Phone, ArrowRight, Home as HomeIcon, Globe2, FileDown, Plane, Briefcase, ExternalLink, Truck, Clock } from "lucide-react";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { StickyCall } from "../components/StickyCall";
import { Marquee } from "../components/Marquee";
import { Faq } from "../components/sections/Faq";
import { Reveal, StaggerLines } from "../components/Reveal";
import { findCountry, COUNTRY_LIST, embassyLink } from "../lib/countries";
import { CountrySearch } from "../components/CountrySearch";
import { SquareCheckout } from "../components/SquareCheckout";
import { getCountryVisa, buildFeeOptions, feeOptionLabel, flagUrl, SHIPPING_OPTIONS } from "../data/visaOrders";
import { PHONE, PHONE_HREF, PHONE2, IMAGES, WORK_ORDER_FORM, SHIPPING } from "../lib/site";

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

export default function CountryVisaPage() {
  const { country } = useParams();
  const data = findCountry(country);
  if (!data) return <Navigate to="/visa-services-washington-dc" replace />;

  const { name, region, slug } = data;
  const regionText = region === "worldwide" ? "worldwide" : `across ${region}`;

  const metaTitle = `${name} Visa Services in Washington DC | Tourist & Business Visa`;
  const metaDescription = `Need a visa for ${name}? UPV Services expedites ${name} tourist and business visa processing in Washington DC with hand-carried embassy filings and fast turnaround. Call 202-457-4665.`;
  const h1 = `${name} Visa Services in Washington DC`;
  const intro = `Traveling to ${name}? UPV Services helps US travelers obtain ${name} tourist and business visas quickly. As a Washington DC visa expediter located in the embassy district, we prepare your application, verify every requirement, and hand-carry your documents to the ${name} embassy or consulate — cutting days off standard timelines.`;

  const bullets = [
    `${name} tourist visa processing`,
    `${name} business visa processing`,
    "Embassy & consulate hand-carry in DC",
    "Application review before submission",
  ];

  const sections = [
    { h2: `Do US citizens need a visa for ${name}?`, body: `Many US travelers require a visa to enter ${name}. Requirements ${regionText} vary by trip purpose and length, and they change frequently. Tell us your travel dates and purpose and we'll confirm exactly what ${name} requires and prepare a precise checklist so your application isn't rejected on a technicality.` },
    { h2: `How fast can you process a ${name} visa?`, body: `Because we're steps from the embassies in downtown Washington DC, we can often submit and retrieve ${name} visa applications far faster than mail-in services. Processing time depends on the ${name} embassy's current schedule, but we prioritize urgent departures and keep you updated at every stage.` },
    { h2: `What documents are needed for a ${name} visa?`, body: `A ${name} visa application typically requires a valid US passport with sufficient validity and blank pages, a completed application form, passport photos, and supporting documents such as an itinerary, invitation, or hotel booking. We review each item before submission to eliminate errors and delays.` },
  ];

  const faqs = [
    { q: `How long does a ${name} visa take to process?`, a: `Processing times for a ${name} visa depend on the embassy's current workload and the visa type. UPV Services expedites submissions and hand-carries documents in Washington DC to secure the fastest available turnaround. Call 202-457-4665 with your travel date for a precise estimate.` },
    { q: `What documents do I need for a ${name} visa?`, a: `You'll generally need a valid US passport, a completed ${name} visa application, passport photos, and supporting documents like a travel itinerary or invitation letter. We provide a tailored checklist once we know your trip details.` },
    { q: `Do you offer both tourist and business visas for ${name}?`, a: `Yes. UPV Services processes both tourist and business visas for ${name}, handling the specific forms and supporting documents each category requires.` },
  ];

  const related = COUNTRY_LIST.filter((c) => c.slug !== slug).slice(0, 6);
  const embassyUrl = embassyLink(slug);
  const visa = getCountryVisa(slug, name);
  const feeOptions = buildFeeOptions(visa);
  const touristReqs = visa.tourist.requirements;
  const businessReqs = visa.business.requirements;
  const checkoutFields = [
    { key: "fee_id", label: "Visa Type & Fees", required: true, options: feeOptions.map((o) => ({ value: o.id, label: feeOptionLabel(o) })) },
    { key: "shipping_id", label: "Return Shipping", options: SHIPPING_OPTIONS.map((o) => ({ value: o.id, label: `${o.label}${o.cost ? ` (+$${o.cost}.00)` : ""}` })) },
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: h1,
      serviceType: `${name} visa expediting`,
      description: metaDescription,
      provider: { "@type": "LocalBusiness", name: "Urgent Passport & Visa", telephone: "+1-202-457-4665" },
      areaServed: ["Washington DC", "Maryland", "Virginia", "United States"],
      url: `https://upvservices.com/visa/${slug}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://upvservices.com/" },
        { "@type": "ListItem", position: 2, name: "Visa Services", item: "https://upvservices.com/visa-services-washington-dc" },
        { "@type": "ListItem", position: 3, name: h1, item: `https://upvservices.com/visa/${slug}` },
      ],
    },
  ];

  return (
    <div data-testid="country-visa-page">
      <Seo title={metaTitle} description={metaDescription} path={`/visa/${slug}`} jsonLd={jsonLd} />
      <Header />
      <main id="main-content" tabIndex={-1}>
        <section className="relative bg-[#0b192c] text-white overflow-hidden" data-testid="country-hero">
          <div className="noise-overlay" />
          <div className="absolute inset-0">
            <img src={IMAGES.traveler} alt={`Travel to ${name}`} className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0b192c] via-[#0b192c]/90 to-[#0b192c]/40" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 md:px-8 pt-16 pb-20 md:pt-24 md:pb-28">
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-8" aria-label="Breadcrumb">
              <Link to="/" onClick={scrollTop} className="hover:text-white inline-flex items-center gap-1"><HomeIcon className="w-3.5 h-3.5" /> Home</Link>
              <span>/</span>
              <Link to="/visa-services-washington-dc" onClick={scrollTop} className="hover:text-white">Visa Services</Link>
              <span>/</span>
              <span className="text-white/90">{name}</span>
            </nav>
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#ef233c]"><Globe2 className="w-4 h-4" /> Visa Services</span>
            <div className="mt-4 flex items-center gap-4">
              {flagUrl(name, 80) && (
                <img src={flagUrl(name, 160)} alt={`${name} flag`} className="w-16 h-auto rounded-md shadow-lg ring-1 ring-white/20 shrink-0" data-testid="country-flag" />
              )}
              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] tracking-tight max-w-3xl">
                <StaggerLines lines={[`${name} Visa Services `, "in Washington DC"]} />
              </h1>
            </div>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }} className="mt-6 text-lg text-white/75 max-w-2xl leading-relaxed">
              {intro}
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.6 }} className="mt-8 flex flex-wrap gap-4">
              <a href="#quote" className="inline-flex items-center gap-2 rounded-full bg-[#d90429] text-white px-7 py-4 font-semibold hover:bg-[#ef233c] transition-colors" data-testid="country-cta-quote">
                Place Your Order <ArrowRight className="w-4 h-4" />
              </a>
              <a href={PHONE_HREF} className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 text-white px-7 py-[14px] font-semibold hover:bg-white hover:text-[#0b192c] transition-colors" data-testid="country-cta-call">
                <Phone className="w-4 h-4" /> Call for a Quote: {PHONE}
              </a>
            </motion.div>
            {slug !== "china" && <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }} className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-2 backdrop-blur-sm" data-testid="processing-badge">
              <Clock className="w-4 h-4 text-[#ef233c]" />
              <span className="text-sm font-semibold text-white/90">Typical processing: 5–7 business days</span>
            </motion.div>}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.6 }} className="mt-8">
              <CountrySearch />
            </motion.div>
          </div>
        </section>

        <Marquee />

        <section className="bg-white py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <Reveal>
                <div className="grid sm:grid-cols-2 gap-4 mb-14">
                  {bullets.map((b, i) => (
                    <div key={i} className="flex items-start gap-3" data-testid={`country-bullet-${i}`}>
                      <span className="shrink-0 w-6 h-6 rounded-full bg-[#d90429]/10 flex items-center justify-center mt-0.5">
                        <Check className="w-3.5 h-3.5 text-[#d90429]" />
                      </span>
                      <span className="text-[15px] text-[#0b192c] font-medium">{b}</span>
                    </div>
                  ))}
                </div>
              </Reveal>
              <div className="space-y-12">
                {sections.map((s, i) => (
                  <Reveal key={i} delay={0.05}>
                    <article>
                      <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0b192c] tracking-tight">{s.h2}</h2>
                      <p className="mt-4 text-slate-600 leading-relaxed text-[17px]">{s.body}</p>
                    </article>
                  </Reveal>
                ))}
              </div>

              {/* Requirements */}
              <Reveal>
                <div className="mt-14" data-testid="visa-requirements">
                  <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#d90429]">Step 1 — Prepare</span>
                  <h2 className="mt-3 font-display font-bold text-2xl md:text-3xl text-[#0b192c] tracking-tight">{name} Visa Requirements</h2>
                  <p className="mt-4 text-slate-600 leading-relaxed text-[17px]">
                    Below is what's required for a {name} tourist or business visa. Choose your visa type and start your application on the right — a specialist confirms your exact checklist and total.
                  </p>

                  <div className="mt-8 grid md:grid-cols-2 gap-6">
                    {/* Tourist */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_4px_24px_rgba(11,25,44,0.05)]" data-testid="tourist-requirements">
                      <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                        <span className="w-10 h-10 rounded-xl bg-[#0b192c] flex items-center justify-center"><Plane className="w-5 h-5 text-white" /></span>
                        <h3 className="font-display font-bold text-xl text-[#0b192c]">Tourist Visa</h3>
                      </div>
                      <ul className="mt-5 space-y-4">
                        {touristReqs.map((r, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="shrink-0 w-5 h-5 rounded-full bg-[#d90429]/10 flex items-center justify-center mt-0.5"><Check className="w-3 h-3 text-[#d90429]" /></span>
                            <span className="min-w-0"><span className="block text-[15px] font-semibold text-[#0b192c]">{r.title}</span>{r.desc && <span className="block text-sm text-slate-500 mt-0.5">{r.desc}</span>}{r.sub && <ul className="mt-1 space-y-0.5">{r.sub.map((s, si) => (<li key={si} className="text-sm text-slate-500 flex gap-1.5"><span className="text-[#d90429]">–</span>{s}</li>))}</ul>}{r.form && <a href={r.form.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#d90429] hover:underline"><FileDown className="w-3.5 h-3.5" /> {r.form.name}</a>}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Business */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_4px_24px_rgba(11,25,44,0.05)]" data-testid="business-requirements">
                      <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                        <span className="w-10 h-10 rounded-xl bg-[#d90429] flex items-center justify-center"><Briefcase className="w-5 h-5 text-white" /></span>
                        <h3 className="font-display font-bold text-xl text-[#0b192c]">Business Visa</h3>
                      </div>
                      <ul className="mt-5 space-y-4">
                        {businessReqs.map((r, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="shrink-0 w-5 h-5 rounded-full bg-[#d90429]/10 flex items-center justify-center mt-0.5"><Check className="w-3 h-3 text-[#d90429]" /></span>
                            <span className="min-w-0"><span className="block text-[15px] font-semibold text-[#0b192c]">{r.title}</span>{r.desc && <span className="block text-sm text-slate-500 mt-0.5">{r.desc}</span>}{r.sub && <ul className="mt-1 space-y-0.5">{r.sub.map((s, si) => (<li key={si} className="text-sm text-slate-500 flex gap-1.5"><span className="text-[#d90429]">–</span>{s}</li>))}</ul>}{r.form && <a href={r.form.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#d90429] hover:underline"><FileDown className="w-3.5 h-3.5" /> {r.form.name}</a>}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {/* Fees tables */}
                  <div className="mt-8" data-testid="visa-fees">
                    <h3 className="font-display font-bold text-xl md:text-2xl text-[#0b192c]">Fees</h3>
                    {[["Tourist", visa.tourist.fees], ["Business", visa.business.fees]].map(([cat, rows]) => (
                      <div key={cat} className="mt-4" data-testid={`fees-${cat.toLowerCase()}`}>
                        <p className="text-sm font-semibold text-[#d90429] uppercase tracking-wide">{cat} Visa</p>
                        <div className="mt-2 overflow-x-auto rounded-xl border border-slate-200">
                          <table className="w-full text-left text-[14px]">
                            <thead>
                              <tr className="bg-[#f8f9fa] text-[#0b192c]">
                                <th className="px-3 py-2.5 font-semibold">Visa Type</th>
                                {slug !== "china" && <th className="px-3 py-2.5 font-semibold">Processing</th>}
                                <th className="px-3 py-2.5 font-semibold">Embassy</th>
                                <th className="px-3 py-2.5 font-semibold">UPV</th>
                                <th className="px-3 py-2.5 font-semibold">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((r, i) => (
                                <tr key={i} className="border-t border-slate-100 text-slate-700">
                                  <td className="px-3 py-2.5">{r.label}</td>
                                  {slug !== "china" && <td className="px-3 py-2.5">{r.processing}</td>}
                                  <td className="px-3 py-2.5">${r.embassy}</td>
                                  <td className="px-3 py-2.5">${r.upv}</td>
                                  <td className="px-3 py-2.5 font-semibold text-[#0b192c]">${r.total}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                    <p className="mt-3 text-xs text-slate-500">Fees shown are estimates. A specialist confirms your exact total. Additional charges may apply for out-of-DC jurisdiction.</p>
                  </div>

                  {/* Work order + embassy links */}
                  <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    <a href={WORK_ORDER_FORM} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3.5 hover:border-[#d90429] hover:bg-[#f8f9fa] transition-colors" data-testid="work-order-form">
                      <FileDown className="w-5 h-5 text-[#d90429] shrink-0" />
                      <span className="text-sm font-medium text-[#0b192c] group-hover:text-[#d90429] transition-colors">UPV Work Order Form (PDF)</span>
                    </a>
                    {embassyUrl && (
                      <a href={embassyUrl} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3.5 hover:border-[#d90429] hover:bg-[#f8f9fa] transition-colors" data-testid="embassy-link">
                        <ExternalLink className="w-5 h-5 text-[#d90429] shrink-0" />
                        <span className="text-sm font-medium text-[#0b192c] group-hover:text-[#d90429] transition-colors">{name} Embassy Website</span>
                      </a>
                    )}
                  </div>

                  {/* Important notes */}
                  <div className="mt-6 rounded-2xl border border-slate-200 bg-[#f8f9fa] p-6" data-testid="visa-notes">
                    <h3 className="font-display font-bold text-lg text-[#0b192c]">Important Notes</h3>
                    <ul className="mt-4 space-y-2.5 text-[15px] text-slate-600">
                      {visa.notes.map((n, i) => (
                        <li key={i} className="flex gap-2"><span className="text-[#d90429]">•</span> {n}</li>
                      ))}
                    </ul>
                  </div>

                  {/* Step 2 shipping */}
                  <div className="mt-6 rounded-2xl bg-[#0b192c] text-white p-6" data-testid="visa-shipping">
                    <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#ef233c]">Step 2 — Ship to us</span>
                    <div className="mt-3 flex items-start gap-3">
                      <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Truck className="w-5 h-5 text-white" /></span>
                      <div>
                        <h3 className="font-display font-bold text-lg">Ship All Materials to UPV Services</h3>
                        <p className="mt-1 text-sm text-white/60">Shipping Method: {SHIPPING.method}</p>
                        <address className="mt-3 not-italic text-[15px] leading-relaxed text-white/90">
                          {SHIPPING.company}<br />
                          {SHIPPING.recipient}<br />
                          {SHIPPING.address}<br />
                          {SHIPPING.cityStateZip}<br />
                          {SHIPPING.phone}
                        </address>
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-24" id="quote">
                <SquareCheckout title={`${name} Visa`} category="visa" service={slug} fields={checkoutFields} defaults={{ shipping_id: "none" }} />
              </div>
            </div>
          </div>
        </section>

        <Faq items={faqs} />

        <section className="bg-[#f8f9fa] py-20 md:py-28" data-testid="country-related">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0b192c] tracking-tight mb-10">Visa services for other countries</h2>
            <div className="flex flex-wrap gap-2.5">
              {related.map((c) => (
                <Link key={c.slug} to={`/visa/${c.slug}`} onClick={scrollTop} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-[#0b192c] hover:border-[#d90429] hover:text-[#d90429] transition-colors" data-testid={`country-related-${c.slug}`}>
                  {c.name} Visa
                </Link>
              ))}
              <Link to="/visa-services-washington-dc" onClick={scrollTop} className="inline-flex items-center gap-1.5 rounded-full bg-[#0b192c] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1a2a40] transition-colors">
                View all countries
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <StickyCall />
    </div>
  );
}
